import type { ActionMeta, MultiValue } from 'react-select';

import theme from '../../../theme';
import { Box } from '../../Rebass';
import Text from '../../Text';
import React from 'react';

const ReactSelect = React.lazy(() => import('react-select'));
const CreatableSelect = React.lazy(() => import('react-select/creatable'));

const variantStyles = (error) => ({
  standard: {},
  outline: {
    border: 'none',
    borderBottom: `1px solid ${
      error ? theme.colors.statusError : theme.colors.baseGrayLight
    }`,
    borderRadius: 0,
  },
});

export interface SelectOption {
  key: string;
  label: string;
  value: string;
}
type Variant = 'standard' | 'outline';
interface DefaultValue {
  key: string;
  label: string;
  value: string;
}

interface SelectProps {
  label?: string;
  options?: SelectOption[];
  onChange?: (
    newValue: string | SelectOption | MultiValue<string | SelectOption>,
    actionMeta: ActionMeta<string | SelectOption>,
  ) => void;
  value?: string | SelectOption | SelectOption[];
  placeholder?: string;
  bg?: string;
  name?: string;
  noOptionsMessage?: string;
  error?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  height?: number;
  disabled?: boolean;
  defaultValue?: DefaultValue[];
  isCreatable?: boolean;
  formatCreateLabel?: (input: string) => string;
  variant?: Variant;
}

const multiValueBgMap = {
  [theme.colors.bgLight]: theme.colors.baseWhite,
  [theme.colors.baseWhite]: theme.colors.bgLight,
  [theme.colors.bgLighter]: theme.colors.baseWhite,
};
const hoverBgMap = {
  [theme.colors.bgLight]: theme.colors.bgLighter,
  [theme.colors.baseWhite]: theme.colors.bgLight,
  [theme.colors.bgLighter]: theme.colors.bgLight,
};

const textStyles = {
  fontFamily: theme.fonts.sans,
  color: theme.colors.textDark,
  fontSize: theme.fontSizes.tiny,
};

const styles = (bg, error, height, variant) => ({
  placeholder: (styles) => ({
    ...styles,
    ...textStyles,
  }),
  control: (styles) => ({
    ...styles,
    backgroundColor: theme.colors[bg] || theme.colors.baseWhite,
    border: error ? `1px solid ${theme.colors.statusError}` : 'none',
    height: height || 'auto',
    minHeight: 48,
    ...variantStyles(error)[variant],
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: `${theme.space[1]}px`,
    height: 'auto',
    flexWrap: 'wrap',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: theme.colors[bg] || theme.colors.baseWhite,
    zIndex: 9999,
  }),
  option: (styles, state) => ({
    ...styles,
    ...textStyles,
    cursor: 'pointer',
    backgroundColor: state.isFocused
      ? hoverBgMap[bg]
      : theme.colors.transparent,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: multiValueBgMap[bg],
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    fontFamily: theme.fonts.sans,
    fontSize: theme.fontSizes.tiny,
    color: theme.colors.textDark,
    padding: '4px 8px',
    backgroundColor: theme.colors.bgLighter,
    borderRadius: 8,
  }),
  multiValueRemove: () => ({
    padding: '0px 4px',
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.textDark,
    backgroundColor: theme.colors.transparent,
    cursor: 'pointer',
  }),
  noOptionsMessage: (styles) => ({
    ...styles,
    ...textStyles,
  }),
  singleValue: (styles) => ({
    ...styles,
    ...textStyles,
  }),
});

const Select = ({
  label,
  options,
  onChange,
  value,
  placeholder,
  noOptionsMessage,
  bg,
  name,
  error,
  isMulti = false,
  isSearchable = false,
  height,
  disabled = false,
  defaultValue,
  isCreatable,
  variant = 'standard',
  formatCreateLabel,
}: SelectProps) => {
  const customStyles = styles(bg, error, height, variant);

  return (
    <Box>
      <Box mb={error ? [1] : [0]}>
        {label && (
          <Box>
            <Text as="label" htmlFor={name} size="small" fontWeight="bold">
              {label}
            </Text>
          </Box>
        )}

        {!isCreatable ? (
          <ReactSelect
            name={name}
            aria-label={label}
            isMulti={isMulti}
            isSearchable={isSearchable}
            onChange={onChange}
            options={options}
            value={value || ''}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionsMessage}
            styles={customStyles}
            classNamePrefix="react-select"
            isDisabled={disabled}
            defaultValue={defaultValue}
            closeMenuOnSelect={!isMulti}
            blurInputOnSelect={!isMulti}
          />
        ) : (
          <CreatableSelect
            isClearable
            name={name}
            aria-label={label}
            isMulti={isMulti}
            isSearchable={isSearchable}
            onChange={onChange}
            options={options}
            value={value || ''}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionsMessage}
            styles={customStyles}
            classNamePrefix="react-select"
            isDisabled={disabled}
            defaultValue={defaultValue}
            formatCreateLabel={formatCreateLabel ?? undefined}
            closeMenuOnSelect={!isMulti}
            blurInputOnSelect={!isMulti}
          />
        )}
      </Box>
      {error && (
        <Text color="statusError" size="small">
          {error}
        </Text>
      )}
    </Box>
  );
};

export default Select;
